/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Represents different screens in the web application
 */
// Transaction data structure for web navigation
data class TransactionData(
    val id: String,
    val merchant: String,
    val amount: Double,
    val date: String,
    val category: String,
    val account: String,
    val isDebit: Boolean,
    val aiConfidence: Int? = null,
    val categorySource: String = "plaid",
    val primaryCategory: String? = null,
    val detailedCategory: String? = null,
    val confidenceLevel: String? = null,
    val categoryLevel1: String = "",
    val categoryLevel2: String = "",
    val categoryLevel3: String = "",
    // Goal assignment fields
    val assignedGoalId: String? = null,
    val assignedGoalName: String? = null,
    val assignedGoalIcon: String? = null,
    /** Every goal this transaction feeds. The three fields above name only the
     *  largest; this is what tells the row it is funding more than one. */
    val assignedGoals: List<models.AssignedGoalShare> = emptyList(),
    // Split/Distribute fields
    val isSplitParent: Boolean = false,
    val isDistributedParent: Boolean = false,
    val parentTransactionId: String? = null,
    val childType: String? = null,
    val distributionStartMonth: String? = null,
    val distributionEndMonth: String? = null,
    // User notes
    val userNotes: String? = null,
    // Exclude flag
    val isExcluded: Boolean = false
)

sealed class Screen {
    object Landing : Screen()
    object Login : Screen()
    data class MfaVerification(val tempToken: String, val userId: String) : Screen()
    object Register : Screen()
    data class CheckEmail(val email: String) : Screen()
    data class AccountLinking(val daysRequested: Int = 90) : Screen()
    data class TransactionList(
        val categoryFilter: String? = null,
        // Set when arriving via an email-link deep link (e.g., wiz3r.com/?tx=<id>);
        // the list auto-opens this transaction's detail once data has loaded.
        val openTransactionId: String? = null,
    ) : Screen()
    data class TransactionDetail(
        val transactionId: String,
        val transactionData: TransactionData? = null
    ) : Screen()
    data class SavingsGoals(val openCreateDialog: Boolean = false) : Screen()
    object Recommendations : Screen()

    /**
     * Wiz 3R personalized advice card detail. `anchor` scrolls to a specific
     * R-section on mount: "reveal" | "reason" | "realize" | null (top).
     * The card payload itself is carried via [WebNavigationManager.selectedWiz3rCard]
     * (same pattern as TransactionDetail / selectedTransaction).
     */
    data class Wiz3RCardDetail(
        val cardId: String,
        val anchor: String? = null,
        /** True when arriving via the dashboard's "Ask Wiz 3R" button — the
         *  chat panel opens automatically on mount instead of waiting for a
         *  second tap. */
        val openChat: Boolean = false,
    ) : Screen()

    // Subscription pages
    data class Subscribe(val proOnly: Boolean = false) : Screen()
    object SubscribeSuccess : Screen()
    object SubscribeCancel : Screen()

    // Auth pages
    object ResetPassword : Screen()
    // emailVerified=true/false for server-side result; verifyToken for legacy JS-side flow
    data class VerifyEmail(val emailVerified: Boolean? = null, val verifyToken: String? = null) : Screen()

    // Import
    // appendToAccountId set => "append more transactions to this existing
    // account" mode (skips the institution/account-name edit step and uses
    // the /import-transactions endpoint). null => "create new manual account
    // from a statement" mode.
    data class StatementImport(val appendToAccountId: String? = null) : Screen()

    // Info pages
    object Guide : Screen()

    // Legal pages
    object Terms : Screen()
    object Privacy : Screen()
    object Security : Screen()
    object About : Screen()
    object Contact : Screen()
    object Team : Screen()
}